// @lymon/widget-sdk-protocol — single source of truth for the
// postMessage envelopes exchanged between host and widget. Imported
// by both the host (services/web) and the client; never bundled
// directly into a widget HTML.
//
// Wire protocol version follows semver. Bump major when adding
// required fields or removing kinds. Bump minor when adding new
// kinds (backward-compatible). Bump patch for documentation-only
// changes.

export const PROTOCOL_VERSION = '1.0' as const;

// ---------------------------------------------------------------------
// Context — broadcast as part of `ready` and again on every change.
// ---------------------------------------------------------------------

export interface WidgetContext {
  protocol: typeof PROTOCOL_VERSION;
  /** Dashboard variables resolved to concrete values. */
  vars: Record<string, unknown>;
  /** Selected time range, or null if dashboard has none. */
  timeRange: {
    start: number;
    end: number;
    iso?: { from: string; to: string };
  } | null;
  /** Pre-computed CSS custom properties from the host theme. */
  theme: Record<string, string>;
  /** BCP-47 locale of the host SPA. */
  locale: string;
  /** Tenant id (informational; widget cannot escalate by forging it). */
  tenant_id: string;
}

// ---------------------------------------------------------------------
// Permissions — declared in manifest, narrowable per-widget instance.
// ---------------------------------------------------------------------

export interface Permissions {
  /** Read data via fetchData/subscribeData (the binding data plane). */
  data: boolean;
  storage: boolean;
  /** false = none, true = all, string[] = whitelist of command ids. */
  commands: false | true | string[];
  setVariable: boolean;
  /** Evaluate Lymon expressions via the host. Default false. */
  eval: boolean;
}

export const DEFAULT_PERMISSIONS: Permissions = {
  data: true,
  storage: true,
  commands: false,
  setVariable: false,
  eval: false,
};

// ---------------------------------------------------------------------
// Data plane — a plugin reads data by handing the host a BINDING, the
// same object a native chart/table/scalar stores in node.dataBinding.
// The host resolves it through the native render path (preview_binding →
// resolveBinding), so a plugin gets the whole data model with no drift:
//   • a single warehouse series          { kind:'series', series_id }
//   • a dimensional query                { kind:'series', metric_name, labels, group_by, agg, step_s }
//   • a connector point / query / history { kind:'point'|'query', connector_id, selection|op|args }
//   • a transform / mixed-source pipeline { kind:'transform', queries, join, transforms }
// The binding shape is owned by the backend; the SDK treats it opaquely.
// ---------------------------------------------------------------------

export type Binding = Record<string, unknown>;

export interface DataQuery {
  /** The binding to resolve (same shape as a native node's dataBinding). */
  binding: Binding;
  /** Window as date-math/ISO (now-1h…now). Omit to use the dashboard window. */
  timeRange?: { from?: string; to?: string };
  /** IANA tz for date-math boundaries. Default UTC. */
  tz?: string;
  /** Return the full series + table (chart/table widgets). Default scalar only. */
  wantSeries?: boolean;
  /** Resolve as a RAW table (all columns as-is). */
  wantTable?: boolean;
}

/**
 * What the data plane returns — the exact shape a native element resolves to
 * (from preview_binding). Common fields below; treat unknown extras as opaque.
 */
export interface DataResult {
  ok: boolean;
  error?: string;
  detail?: string;
  /** Scalar value (default mode). */
  value?: number | string | null;
  unit?: string;
  /** Series for charts (wantSeries). */
  series?: Array<{ key?: string; label?: string; points: Array<{ ts: number; value: number | null }> }>;
  /** Tabular result (wantSeries/wantTable). */
  table?: { columns: Array<{ key: string; label: string }>; rows: unknown[][] };
  [extra: string]: unknown;
}

// ---------------------------------------------------------------------
// Host → Widget messages
// ---------------------------------------------------------------------

export type HostMessage =
  | { kind: 'ready'; protocol: typeof PROTOCOL_VERSION; context: WidgetContext; storage: Record<string, unknown> }
  | { kind: 'context'; context: WidgetContext }
  | { kind: 'data-result'; reqId: string; result: DataResult }
  | { kind: 'data-error'; reqId: string; message: string }
  | { kind: 'data-value'; subId: string; result: DataResult }
  | { kind: 'data-sub-error'; subId: string; message: string }
  | { kind: 'command-result'; reqId: string; result: unknown }
  | { kind: 'command-error'; reqId: string; message: string }
  | { kind: 'set-variable-result'; reqId: string }
  | { kind: 'set-variable-error'; reqId: string; message: string }
  | { kind: 'eval-result'; reqId: string; value: number | string | boolean | null }
  | { kind: 'eval-error'; reqId: string; message: string }
  /** Sent once when the widget should clean up before unmount. */
  | { kind: 'destroy' };

// ---------------------------------------------------------------------
// Widget → Host messages
// ---------------------------------------------------------------------

export type WidgetMessage =
  | { kind: 'ready'; sdkVersion: string }
  | { kind: 'data'; reqId: string; query: DataQuery }
  | { kind: 'data-subscribe'; subId: string; query: DataQuery; intervalMs?: number }
  | { kind: 'data-unsubscribe'; subId: string }
  | { kind: 'command'; reqId: string; id: string; args: unknown }
  | { kind: 'set-variable'; reqId: string; name: string; value: unknown }
  | { kind: 'eval'; reqId: string; expression: string; refs: Record<string, unknown> }
  | { kind: 'storage-set'; key: string; value: unknown }
  | { kind: 'storage-remove'; key: string }
  | { kind: 'log'; level: 'log' | 'info' | 'warn' | 'error' | 'debug'; args: unknown[] };

// ---------------------------------------------------------------------
// Type guards — keep these in sync with the unions above. Anything
// the host receives is treated as untrusted: use isWidgetMessage()
// before dispatching to a handler.
// ---------------------------------------------------------------------

const WIDGET_KINDS = new Set([
  'ready',
  'data',
  'data-subscribe',
  'data-unsubscribe',
  'command',
  'set-variable',
  'eval',
  'storage-set',
  'storage-remove',
  'log',
]);

export function isWidgetMessage(x: unknown): x is WidgetMessage {
  return (
    typeof x === 'object' &&
    x !== null &&
    typeof (x as { kind?: unknown }).kind === 'string' &&
    WIDGET_KINDS.has((x as { kind: string }).kind)
  );
}

const HOST_KINDS = new Set([
  'ready',
  'context',
  'data-result',
  'data-error',
  'data-value',
  'data-sub-error',
  'command-result',
  'command-error',
  'set-variable-result',
  'set-variable-error',
  'eval-result',
  'eval-error',
  'destroy',
]);

export function isHostMessage(x: unknown): x is HostMessage {
  return (
    typeof x === 'object' &&
    x !== null &&
    typeof (x as { kind?: unknown }).kind === 'string' &&
    HOST_KINDS.has((x as { kind: string }).kind)
  );
}
